import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import Pagination from "../../components/Pagination";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { FormDialog, inputCls, labelCls } from "../../features/web3/forex/components/FormDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { formatDateTime } from "../../lib/format";
import {
  STATUS_OPTIONS,
  mediaUrl,
  useBlogList,
  useBlogStatus,
  useBlogTaxonomy,
  useDeleteBlog,
} from "./api";

const PAGE_SIZES = [10, 15, 25, 50, 100];

const DEFAULT_FILTERS = { page: 1, pageSize: 15, search: "", category: "", tag: "", status: "", isFeatured: "" };

const COLUMNS = [
  { label: "Cover" },
  { label: "Title" },
  { label: "Slug" },
  { label: "Category" },
  { label: "Tags" },
  { label: "Author" },
  { label: "Status" },
  { label: "Featured" },
  { label: "Views", align: "right" },
  { label: "Read time", align: "right" },
  { label: "Published" },
  { label: "Created by" },
  { label: "Updated by" },
  { label: "Updated" },
  { label: "", align: "right", sticky: true },
];

export default function Blogs() {
  const { can } = usePermissions();
  const canCreate = can("POST:/api/admin/blogs");
  const canEdit = can("POST:/api/admin/blogs/:id/edit");
  const canDelete = can("POST:/api/admin/blogs/:id/delete");
  const canStatus = can("POST:/api/admin/blogs/:id/status");

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const list = useBlogList(filters);
  const taxonomy = useBlogTaxonomy();
  const setStatus = useBlogStatus();
  const remove = useDeleteBlog();
  const [deleting, setDeleting] = useState(null);

  const rows = list.data?.data;

  const setField = (e) => setDraft((d) => ({ ...d, [e.target.name]: e.target.value }));
  const applyFilters = () => setFilters({ ...draft, page: 1 });
  const clearFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };
  const goToPage = (page) => {
    setDraft((d) => ({ ...d, page }));
    setFilters((f) => ({ ...f, page }));
  };

  const changeStatus = async (row, status) => {
    try {
      await setStatus.mutateAsync({ id: row._id, status });
      toast.success("Status updated");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status change failed");
    }
  };

  const runDelete = async () => {
    try {
      await remove.mutateAsync(deleting._id);
      toast.success("Post deleted");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
    setDeleting(null);
  };

  return (
    <QueueLayout
      eyebrow="Content"
      title="Blog"
      description="Posts shown on the website. Only published ones are public."
      isFetching={list.isFetching}
      onRefresh={() => {
        list.refetch();
        taxonomy.refetch();
      }}
    >
      <div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div className="xl:col-span-2">
            <label className={labelCls}>Search</label>
            <input
              name="search"
              value={draft.search}
              onChange={setField}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Title, summary, category, tag or author"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select name="category" value={draft.category} onChange={setField} className={inputCls}>
              <option value="">All categories</option>
              {taxonomy.data?.categories?.map((c) => (
                <option key={c.category} value={c.category}>
                  {c.category} ({c.published}/{c.total} published)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Tag</label>
            <select name="tag" value={draft.tag} onChange={setField} className={inputCls}>
              <option value="">All tags</option>
              {taxonomy.data?.tags?.map((t) => (
                <option key={t.tag} value={t.tag}>
                  {t.tag} ({t.count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select name="status" value={draft.status} onChange={setField} className={inputCls}>
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Featured</label>
            <select name="isFeatured" value={draft.isFeatured} onChange={setField} className={inputCls}>
              <option value="">All</option>
              <option value="true">Featured</option>
              <option value="false">Not featured</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={draft.pageSize} onChange={setField} className={inputCls}>
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green transition-colors hover:bg-ginox-green/20"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
          >
            Clear Filters
          </button>
          {canCreate ? (
            <Button size="sm" className="ml-auto" asChild>
              <Link to="/blogs/new">
                <Plus className="h-3.5 w-3.5" />
                New post
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={COLUMNS}
            isLoading={list.isLoading}
            rows={rows}
            emptyText="No posts found."
            renderRow={(row) => (
              <tr key={row._id} className="transition-colors hover:bg-white/[0.02]">
                <Td>
                  {row.coverImage ? (
                    <img src={mediaUrl(row.coverImage)} alt="" className="h-10 w-16 rounded object-cover" />
                  ) : (
                    <span className="text-muted-foreground/40">—</span>
                  )}
                </Td>
                <Td className="min-w-[220px] max-w-[320px] whitespace-normal font-medium">{row.title}</Td>
                <Td className="font-mono text-xs text-muted-foreground">{row.slug}</Td>
                <Td className="text-muted-foreground">{row.category}</Td>
                <Td className="max-w-[200px] whitespace-normal text-xs text-muted-foreground">
                  {row.tags?.length ? row.tags.join(", ") : "—"}
                </Td>
                <Td className="whitespace-nowrap text-muted-foreground">{row.author}</Td>
                <Td>
                  <select
                    value={row.status}
                    disabled={!canStatus || setStatus.isPending}
                    onChange={(e) => changeStatus(row, e.target.value)}
                    className={`${inputCls} w-auto py-1 text-xs`}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Td>
                <Td className="text-muted-foreground">{row.isFeatured ? "Yes" : "No"}</Td>
                <Td align="right" className="font-mono">{row.views ?? 0}</Td>
                <Td align="right" className="whitespace-nowrap font-mono text-muted-foreground">{row.readTimeMinutes} min</Td>
                <Td className="whitespace-nowrap font-mono text-muted-foreground">
                  {row.publishedAt ? formatDateTime(row.publishedAt) : "—"}
                </Td>
                <Td className="text-muted-foreground">{row.createdBy || "—"}</Td>
                <Td className="text-muted-foreground">{row.updatedBy || "—"}</Td>
                <Td className="whitespace-nowrap font-mono text-muted-foreground">{formatDateTime(row.updatedAt)}</Td>
                <Td align="right" sticky>
                  <div className="flex justify-end gap-2">
                    {canEdit ? (
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/blogs/${row._id}/edit`}>
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Link>
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button size="sm" variant="outline" onClick={() => setDeleting(row)}>
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    ) : null}
                  </div>
                </Td>
              </tr>
            )}
          />

          <Pagination
            currentPage={Number(list.data?.currentPage) || filters.page}
            totalPages={list.data?.totalPages ?? 1}
            totalRows={list.data?.totalRows ?? 0}
            pageSize={Number(filters.pageSize)}
            onPageChange={goToPage}
            label="posts"
          />
        </>
      )}

      <FormDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this post?"
        description={deleting?.title}
        onSubmit={runDelete}
        submitLabel="Delete"
        loading={remove.isPending}
        danger
      />
    </QueueLayout>
  );
}
